/**
 * artifactRecords — 학습지/PPT 산출물 레코드 계약 (backend/services/artifact_records.py와 동일)
 *
 * 같은 모양의 레코드를 세 곳에 그대로 넣는다 (UI 연결은 WP-B/E):
 * 1. 대화 문서 conversations/{id}.worksheetArtifacts[] / .pptArtifacts[]
 * 2. 내 자료 user_content/{id}.artifact
 * 3. 채팅 메시지 messages/{id}.artifacts[]
 *
 * 레코드 모양 (camelCase, Firestore/JSON 저장용)
 * {
 *   recordVersion: 1,
 *   id: 'art_3f9c2a1b7d4e',
 *   kind: 'worksheet' | 'ppt' | null,
 *   format: 'pdf' | 'pptx' | 'docx' | 'hwpx' | 'png',
 *   filename: '광합성_학습지.pdf',
 *   downloadUrl: 'https://... 또는 /api/...',
 *   pageCount: 3,
 *   previewUrls: ['https://.../p1.png'],
 *   sourceMode: 'text' | 'image' | 'hybrid',
 *   canvasVersionId: 'cv_...' | null,     // outlineVersions 문서 id (canvas 포함)
 *   outlineVersionId: 'ov_...' | null,    // outlineVersions 문서 id
 *   createdAt: '2026-09-11T00:12:34.567Z', // ISO 8601 UTC
 *   sizeBytes?: 123456,
 *   derivedFromArtifactId?: 'art_...',    // 선택. 반대 모드로 파생된 산출물의 원본 id
 *   outlineFingerprint?: string,          // 선택. 개요 내용 지문(테마·쪽수 제외)
 *   contentItemCount?: number,            // 선택. 문항/슬라이드 수
 * }
 */

export const ARTIFACT_RECORD_VERSION = 1;
export const ARTIFACT_FORMATS = new Set(['pdf', 'pptx', 'docx', 'hwpx', 'png']);
export const ARTIFACT_SOURCE_MODES = new Set(['text', 'image', 'hybrid']);
export const ARTIFACT_KINDS = new Set(['worksheet', 'ppt']);

export const CHAT_CATEGORY_TO_ARTIFACT_KIND = Object.freeze({
  worksheet_gen: 'worksheet',
  worksheet: 'worksheet',
  ppt_maker: 'ppt',
  ppt: 'ppt',
});

export const MAX_FILENAME_LENGTH = 255;
export const MAX_PREVIEW_URLS = 200;
export const MAX_PAGE_COUNT = 1000;

const ID_RE = /^[A-Za-z0-9_-]{6,80}$/;

export class ArtifactRecordError extends Error {
  constructor(message) {
    super(message);
    this.name = 'ArtifactRecordError';
  }
}

function randomHex(length) {
  const cryptoObj = typeof window !== 'undefined' ? window.crypto : undefined;
  if (cryptoObj && typeof cryptoObj.getRandomValues === 'function') {
    const bytes = new Uint8Array(Math.ceil(length / 2));
    cryptoObj.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('').slice(0, length);
  }
  let out = '';
  while (out.length < length) out += Math.floor(Math.random() * 16).toString(16);
  return out.slice(0, length);
}

export function newArtifactId() {
  return `art_${randomHex(12)}`;
}

function toIsoUtc(value) {
  if (value == null) return new Date().toISOString();
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) throw new ArtifactRecordError('createdAt is an invalid Date');
    return value.toISOString();
  }
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return new Date(value).toISOString();
  // Firestore Timestamp
  if (value && typeof value.toDate === 'function') return value.toDate().toISOString();
  throw new ArtifactRecordError(`createdAt must be Date, ISO string or epoch ms, got ${typeof value}`);
}

function cleanUrls(urls) {
  if (!Array.isArray(urls)) return [];
  return urls
    .filter((u) => typeof u === 'string' && u.trim())
    .map((u) => u.trim())
    .slice(0, MAX_PREVIEW_URLS);
}

function isNonNegativeInt(value) {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

function optionalArtifactId(value, fieldName) {
  if (value == null || value === '') return null;
  const text = String(value).trim();
  if (!text) return null;
  if (!ID_RE.test(text)) {
    throw new ArtifactRecordError(`${fieldName} must match ${ID_RE}, got ${value}`);
  }
  return text;
}

export function artifactKindForCategory(category) {
  if (!category) return null;
  return CHAT_CATEGORY_TO_ARTIFACT_KIND[String(category).trim().toLowerCase()] || null;
}

/**
 * 산출물 레코드 조립. 필수값이 잘못되면 ArtifactRecordError.
 * @param {{
 *   format: string, filename: string, downloadUrl: string, pageCount: number,
 *   previewUrls?: string[], sourceMode: string,
 *   canvasVersionId?: string|null, outlineVersionId?: string|null,
 *   createdAt?: Date|string|number, sizeBytes?: number, id?: string, kind?: string,
 *   derivedFromArtifactId?: string|null,
 *   outlineFingerprint?: string,
 *   contentItemCount?: number,
 *   studentPageCount?: number, answerGuidePageCount?: number,
 * }} input
 */
export function buildArtifactRecord(input = {}) {
  const format = String(input.format || '').trim().toLowerCase();
  if (!ARTIFACT_FORMATS.has(format)) {
    throw new ArtifactRecordError(`format must be one of ${[...ARTIFACT_FORMATS].join('|')}, got ${input.format}`);
  }
  let filename = String(input.filename || '').trim();
  if (!filename) throw new ArtifactRecordError('filename is required');
  if (filename.length > MAX_FILENAME_LENGTH) filename = filename.slice(0, MAX_FILENAME_LENGTH);

  const downloadUrl = String(input.downloadUrl || '').trim();
  if (!downloadUrl) throw new ArtifactRecordError('downloadUrl is required');

  if (!isNonNegativeInt(input.pageCount)) {
    throw new ArtifactRecordError(`pageCount must be int >= 0, got ${input.pageCount}`);
  }
  const sourceMode = String(input.sourceMode || '').trim().toLowerCase();
  if (!ARTIFACT_SOURCE_MODES.has(sourceMode)) {
    throw new ArtifactRecordError(`sourceMode must be one of ${[...ARTIFACT_SOURCE_MODES].join('|')}, got ${input.sourceMode}`);
  }

  let kind = null;
  if (input.kind != null) {
    kind = artifactKindForCategory(input.kind);
    if (!kind) throw new ArtifactRecordError(`kind must be one of ${[...ARTIFACT_KINDS].join('|')}, got ${input.kind}`);
  }

  if (input.sizeBytes != null && !isNonNegativeInt(input.sizeBytes)) {
    throw new ArtifactRecordError(`sizeBytes must be int >= 0, got ${input.sizeBytes}`);
  }

  const derivedFromArtifactId = optionalArtifactId(input.derivedFromArtifactId, 'derivedFromArtifactId');

  const id = input.id ? String(input.id).trim() : newArtifactId();
  if (!ID_RE.test(id)) throw new ArtifactRecordError(`id must match ${ID_RE}, got ${input.id}`);

  const record = {
    recordVersion: ARTIFACT_RECORD_VERSION,
    id,
    kind,
    format,
    filename,
    downloadUrl,
    pageCount: Math.min(input.pageCount, MAX_PAGE_COUNT),
    previewUrls: cleanUrls(input.previewUrls),
    sourceMode,
    canvasVersionId: input.canvasVersionId ? String(input.canvasVersionId) : null,
    outlineVersionId: input.outlineVersionId ? String(input.outlineVersionId) : null,
    createdAt: toIsoUtc(input.createdAt),
  };
  if (input.sizeBytes != null) record.sizeBytes = input.sizeBytes;
  if (derivedFromArtifactId) record.derivedFromArtifactId = derivedFromArtifactId;
  // 한글(hwpx) 산출물이 자료함(chat_file_artifacts)에 등록된 경우 — 우측 rhwp 편집 패널을 열기 위한 경로.
  //   로그인 사용자만 값이 있고(익명은 null), 채팅 복귀 시 패널 재오픈에도 쓴다.
  const artifactId = typeof input.artifactId === 'string' ? input.artifactId.trim() : '';
  if (artifactId) record.artifactId = artifactId;
  const editorPath = typeof input.editorPath === 'string' ? input.editorPath.trim() : '';
  if (editorPath) record.editorPath = editorPath;
  // 학습지 단 수(1|2) — 2단은 rhwp 패널이 한 줄로 보여 주므로 복귀 시 안내(worksheetPanelLayoutNotice)에 쓴다.
  const columns = Number(input.columns);
  if (columns === 1 || columns === 2) record.columns = columns;
  const outlineFingerprint = typeof input.outlineFingerprint === 'string'
    ? input.outlineFingerprint.trim()
    : '';
  if (outlineFingerprint) record.outlineFingerprint = outlineFingerprint;
  const contentItemCount = Number(input.contentItemCount);
  if (Number.isInteger(contentItemCount) && contentItemCount > 0) {
    record.contentItemCount = contentItemCount;
  }
  const fingerprintVersion = Number(input.fingerprintVersion);
  if (Number.isInteger(fingerprintVersion) && fingerprintVersion > 0) {
    record.fingerprintVersion = fingerprintVersion;
  }
  // 학습지 PDF 는 학생용 뒤에 정답·해설이 붙는다 — 교사가 말한 "N쪽"은 학생용 쪽수다.
  const studentPageCount = Number(input.studentPageCount);
  const answerGuidePageCount = Number(input.answerGuidePageCount);
  if (Number.isInteger(studentPageCount) && studentPageCount > 0 && studentPageCount <= record.pageCount
    && Number.isInteger(answerGuidePageCount) && answerGuidePageCount > 0) {
    record.studentPageCount = studentPageCount;
    record.answerGuidePageCount = answerGuidePageCount;
  }
  return record;
}

/** 오류 메시지 배열 (빈 배열이면 유효). 백엔드 validate_artifact_record와 같은 규칙. */
export function validateArtifactRecord(record) {
  const errors = [];
  if (!record || typeof record !== 'object' || Array.isArray(record)) return ['record must be object'];

  if (record.recordVersion != null && record.recordVersion !== ARTIFACT_RECORD_VERSION) {
    errors.push(`recordVersion unsupported: ${record.recordVersion}`);
  }
  if (typeof record.id !== 'string' || !ID_RE.test(record.id)) errors.push('id missing or invalid');
  if (record.kind != null && !ARTIFACT_KINDS.has(record.kind)) errors.push(`kind invalid: ${record.kind}`);
  if (!ARTIFACT_FORMATS.has(record.format)) errors.push(`format invalid: ${record.format}`);

  if (typeof record.filename !== 'string' || !record.filename.trim()) errors.push('filename missing');
  else if (record.filename.length > MAX_FILENAME_LENGTH) errors.push('filename too long');

  if (typeof record.downloadUrl !== 'string' || !record.downloadUrl.trim()) errors.push('downloadUrl missing');
  if (!isNonNegativeInt(record.pageCount)) errors.push('pageCount must be int >= 0');

  if (!Array.isArray(record.previewUrls)) errors.push('previewUrls must be array');
  else if (record.previewUrls.some((u) => typeof u !== 'string')) errors.push('previewUrls must contain strings');
  else if (record.previewUrls.length > MAX_PREVIEW_URLS) errors.push('previewUrls too many');

  if (!ARTIFACT_SOURCE_MODES.has(record.sourceMode)) errors.push(`sourceMode invalid: ${record.sourceMode}`);

  ['canvasVersionId', 'outlineVersionId', 'derivedFromArtifactId'].forEach((key) => {
    if (record[key] != null && typeof record[key] !== 'string') errors.push(`${key} must be string or null`);
    else if (key === 'derivedFromArtifactId' && typeof record[key] === 'string' && !ID_RE.test(record[key])) {
      errors.push('derivedFromArtifactId missing or invalid');
    }
  });

  if (typeof record.createdAt !== 'string' || !record.createdAt.trim()) errors.push('createdAt must be ISO string');

  if (record.sizeBytes != null && !isNonNegativeInt(record.sizeBytes)) errors.push('sizeBytes must be int >= 0');
  return errors;
}

export function isArtifactRecord(record) {
  return validateArtifactRecord(record).length === 0;
}

/** 같은 산출물(id 기준)을 최신으로 갈아 끼우고 createdAt 내림차순으로 정렬한 새 배열. */
export function upsertArtifactRecord(list, record, { limit = 20 } = {}) {
  const base = Array.isArray(list) ? list.filter((r) => r && r.id !== record.id) : [];
  const next = [record, ...base];
  next.sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
  return next.slice(0, limit);
}

const artifactRecords = {
  ARTIFACT_RECORD_VERSION,
  ARTIFACT_FORMATS,
  ARTIFACT_SOURCE_MODES,
  ARTIFACT_KINDS,
  buildArtifactRecord,
  validateArtifactRecord,
  isArtifactRecord,
  upsertArtifactRecord,
  artifactKindForCategory,
  newArtifactId,
};

export default artifactRecords;
